import React, { useState, useEffect } from "react";
import "../../styles/cards/card.css";
import PrimaryButton from "../buttons/PrimaryButton";
import axios from "axios";
import Cookies from "js-cookie";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Modal from "../modal/Modal";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import "../../styles/recordedData/recordedData.css";
import "../../styles/cards/card.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import confirmIcon from "../../assets/img/iconamoon--shield-yes-fill.svg";

const TotalCountsCard = ({ siteId }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [bookTotals, setBookTotals] = useState({
    count: 0,
    yellowFormCount: 0,
    overallTotal: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [estimatedBookEntries, setEstimatedBookEntries] = useState([]);
  const [availableBooks, setAvailableBooks] = useState([]);
  const [formData, setFormData] = useState({
    totalCount: "",
    yellowFormCount: "",
  });
  const [activeTab, setActiveTab] = useState("approved");
  const [submitting, setSubmitting] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [yellowFormBookId, setYellowFormBookId] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  const fetchTotals = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("token");
      const response = await axios.get(
        `${baseUrl}/api/sites/${siteId}/book-totals`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBookTotals({
        count: response.data.count || 0,
        yellowFormCount: response.data.yellowFormCount || 0,
        overallTotal: response.data.overallTotal || 0,
      });
    } catch (error) {
      setError("Failed to fetch totals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTotals();
  }, [showModal, siteId]);

  useEffect(() => {
    if (showDeleteSuccess) {
      const timer = setTimeout(() => {
        setShowDeleteSuccess(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showDeleteSuccess]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddBook = async (e) => {
    e.stopPropagation();

    // Validation based on active tab
    if (activeTab === "approved") {
      if (!formData.totalCount) {
        setError("Please enter book count");
        return;
      }
    } else if (activeTab === "all" && !formData.yellowFormCount) {
      setError("Please enter a yellow form count");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const token = Cookies.get("token");

      const payload =
        activeTab === "approved"
          ? {
              count: parseInt(formData.totalCount),
            }
          : {
              yellowFormCount: parseInt(formData.yellowFormCount),
            };

      const response = await axios.post(
        `${baseUrl}/api/sites/${siteId}/add/received-books`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 201 || response.status === 200) {
        setFormData({
          totalCount: "",
          yellowFormCount: "",
        });
        // setShowConfirmationModal(true);
        await fetchTotals();
      }
    } catch (error) {
      console.error("Error adding book count:", error);
      setError("Error adding entry");
    } finally {
      setSubmitting(false);
    }
  };

  // Update existing book or yellow form
  const handleUpdateBook = async (e) => {
    e.stopPropagation();
    if (!editingEntry) return;

    try {
      setSubmitting(true);
      setError("");
      const token = Cookies.get("token");
      const updateData = {};

      if (activeTab === "approved" && formData.totalCount !== "") {
        updateData.count = parseInt(formData.totalCount);
      } else if (activeTab === "all" && formData.yellowFormCount !== "") {
        updateData.yellowFormCount = parseInt(formData.yellowFormCount);
      }

      if (!updateData.count && !updateData.yellowFormCount) {
        setError("Please provide a count to update");
        return;
      }

      const response = await axios.put(
        `${baseUrl}/api/sites/${siteId}/books`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        setFormData({
          totalCount: "",
          yellowFormCount: "",
        });
        setEditingEntry(null);
        await fetchTotals();
      }
    } catch (error) {
      setError("Error updating book entry");
    } finally {
      setSubmitting(false);
    }
  };


  // Delete book or yellow form
  const handleDeleteBook = async () => {
    if (!recordToDelete || !recordToDelete.bookId) {
      setError("No valid book entry selected for deletion");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      const token = Cookies.get("token");
      const deleteData = {
        category: recordToDelete.category,
        type: recordToDelete.bookType || null,
      };

      const response = await axios.delete(
        `${baseUrl}/api/sites/${siteId}/books/${recordToDelete.bookId}`,
        { headers: { Authorization: `Bearer ${token}` }, data: deleteData }
      );

      if (response.status === 200) {
        setShowDeleteConfirm(false);
        setShowDeleteSuccess(true);
        setRecordToDelete(null);
        await fetchTotals();
      }
    } catch (error) {
      setError("Error deleting entry");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (entry, tab) => {
    setEditingEntry({
      ...entry,
    });
    setFormData({
      totalCount: tab === "approved" ? entry.bookCount || "" : "",
      yellowFormCount: tab === "all" ? entry.yellowFormCount || "" : "",
    });
    setActiveTab(tab);
  };

  const handleDeleteClick = (entry) => {
    const bookId =
      entry.category === "Yellow Forms" ? yellowFormBookId : entry.bookId;
    if (!bookId) {
      setError("Cannot delete: Book ID is not available");
      return;
    }
    setRecordToDelete({
      ...entry,
      bookId: bookId,
    });
    setShowDeleteConfirm(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      category: "",
      type: "",
      totalCount: "",
      yellowFormCount: "",
    });
    setEditingEntry(null);
    setError("");
  };

  const formatBookType = (type) => {
    if (!type) return "";
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  };

  return (
    <div className="total-counts-container">
      <div className="total-counts-card">
        {loading ? (
          <div className="count-rows">
            {Array.from({ length: 1 }).map((_, index) => (
              <div key={index} className="count-row">
                <Skeleton width={70} height={16} />
                <Skeleton
                  width={80}
                  height={24}
                  style={{ borderRadius: "35px" }}
                />
                <Skeleton width={10} height={16} />
                <Skeleton width={20} height={16} />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <>
            <div className="count-rows">
              <div className="total-books">
              Total Books: <span className="count">{bookTotals.count}</span>
            </div>
            <div className="yellow-form">
              Yellow Forms: <span className="count">{bookTotals.yellowFormCount}</span>
            </div>
            </div>

            
          </>
        )}
      </div>
      <PrimaryButton
        text="Edit Information"
        onClick={() => setShowModal(true)}
      /> 

      <Modal show={showModal} onClose={handleCloseModal}>
        <div className="title initialized">UPDATE BOOKS</div>
        <div className="content">
          <div className="form-container new-records">
            <form action="">
              <div className="scanned-books initialized">
                <div className="tabs">
                  <div
                    className={`tab ${
                      activeTab === "approved" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("approved")}
                  >
                    CR Books
                  </div>
                  <div
                    className={`tab ${activeTab === "all" ? "active" : ""}`}
                    onClick={() => setActiveTab("all")}
                  >
                    Yellow Forms
                  </div>
                </div>
                <div className="data">
                  {activeTab === "approved" ? (
                    <>
                      <div className="label">
                        <input
                          placeholder="Book Count"
                          type="number"
                          name="totalCount"
                          value={formData.totalCount}
                          onChange={handleInputChange}
                          min="1"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="label">
                      <input
                        placeholder="Yellow Form Count"
                        type="number"
                        name="yellowFormCount"
                        value={formData.yellowFormCount}
                        onChange={handleInputChange}
                        min="1"
                      />
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="button-group">
            <PrimaryButton
              text={
                submitting ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : editingEntry ? (
                  "Update"
                ) : (
                  "Save and Continue"
                )
              }
              onClick={editingEntry ? handleUpdateBook : handleAddBook}
              disabled={submitting}
            />
            <PrimaryButton
              text="Cancel"
              onClick={handleCloseModal}
              disabled={submitting}
            />
          </div>
          {(activeTab === "approved"
            ? bookTotals.count > 0
            : bookTotals.yellowFormCount > 0) && (
            <>
              <div className="table-container initialize-site" style={{maxHeight: '300px'}}>
                                <div className="table-padding-wrapper">
                                  <table>
                                    <thead>
                                      <tr>
                                        {(activeTab === "approved" ? (
                                          <th>Total Scanned Records</th>
                                        ) : (
                                          <th>Total Yellow Forms</th>
                                        )
                                        )}
                                        <th>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {loading ? (
                                        Array.from({ length: 2 }).map((_, index) => (
                                          <tr key={index}>
                                            <td>
                                              <Skeleton width={20} height={20} />
                                            </td>
                                            <td>
                                              <Skeleton width={100} />
                                            </td>
                                            <td>
                                              <Skeleton width={80} />
                                            </td>
                                          </tr>
                                        ))
                                      ) : activeTab === "approved" ? (
                                        bookTotals.count > 0 ? (
                                            <tr>
                                              <td>{bookTotals.count}</td>
              
                                              <td>
                                                <div className="edit-delete">
                                                  <a
                                                    onClick={() => handleEditClick({
                                                      bookCount: bookTotals.count,
                                                    }, "approved")} 
                                                    aria-label="Edit"
                                                    className="icon-button edit-button"
                                                    disabled={submitting}
                                                  >
                                                    <Icon
                                                      icon="mdi:book-edit"
                                                      width="20"
                                                      height="20"
                                                      color="#4E7FFF"
                                                    />
                                                  </a>
                                                  {/* <a
                                                    onClick={() => handleDeleteClick({
                                                      bookCount: bookTotals.count,
                                                    })}
                                                    aria-label="Delete"
                                                    className="icon-button delete-button"
                                                    disabled={submitting}
                                                  >
                                                    <Icon
                                                      icon="mingcute:delete-fill"
                                                      width="20"
                                                      height="20"
                                                      color="#E20000"
                                                    />
                                                  </a> */}
                                                </div>
                                              </td>
                                            </tr>
                                        ) : (
                                          <tr>
                                            <td colSpan="4">No books available</td>
                                          </tr>
                                        )
                                      ) : bookTotals.yellowFormCount > 0 ? (
                                        <tr>
                                          <td>{bookTotals.yellowFormCount}</td>
                                          <td>
                                            <div className="edit-delete">
                                              <a
                                                onClick={() =>
                                                  handleEditClick({
                                                    yellowFormCount: bookTotals.yellowFormCount,
                                                  }, "all")
                                                }
                                                aria-label="Edit"
                                                className="icon-button edit-button"
                                                disabled={submitting}
                                              >
                                                <Icon
                                                  icon="mdi:book-edit"
                                                  width="20"
                                                  height="20"
                                                  color="#4E7FFF"
                                                />
                                              </a>
                                              {/* <a
                                                onClick={() =>
                                                  handleDeleteClick({
                                                    category: "Yellow Forms",
                                                    bookType: null,
                                                  })
                                                }
                                                aria-label="Delete"
                                                className="icon-button delete-button"
                                                disabled={submitting}
                                              >
                                                <Icon
                                                  icon="mingcute:delete-fill"
                                                  width="20"
                                                  height="20"
                                                  color="#E20000"
                                                />
                                              </a> */}
                                            </div>
                                          </td>
                                        </tr>
                                      ) : (
                                        <tr>
                                          <td colSpan="3">No yellow forms available</td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
            </>
          )}
        </div>
      </Modal>

      <Modal
        show={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
      >
        <div className="content confirmation">
          <h2>Confirm Delete</h2>
          <div className="text">
            Are you sure you want to delete this{" "}
            {activeTab === "approved" ? "book" : "yellow form"} entry?
          </div>
          <div className="button-group">
            <PrimaryButton
              text={
                submitting ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : (
                  "Yes"
                )
              }
              onClick={handleDeleteBook}
              disabled={submitting}
            />
            <PrimaryButton
              text="No"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={submitting}
            />
          </div>
        </div>
      </Modal>

      <Modal
        show={showDeleteSuccess}
        onClose={() => setShowDeleteSuccess(false)}
      >
        <div className="content confirmation">
          <div className="icon-container">
            <img src={confirmIcon} alt="confirm" width="57" height="57" />
          </div>
          <div className="text">Book Entry has been successfully deleted!</div>
        </div>
      </Modal>
    </div>
  );
};

export default TotalCountsCard;
